import { useApolloClient, useQuery } from "@apollo/client"
import { getErrorCodes } from "@artists/devkit"
import {
	CustomErrorResponses,
	VotesDocument,
} from "@artists/gql/design-challenges"
import { useTypedParams } from "react-router-typesafe-routes/dom"

import type { ApolloErrorContext } from "../../../../../@common"
import { Root, useAnalytics } from "../../../../utils"
import { useAuth } from "../../@shared"
import { normalizeEntry } from "./votes.schema"
import { prepareAnalytics } from "./votes.utils"

export const DEFAULT_ENTRIES_LIMIT = 84
export const useVotesQuery = (offset: number | null) => {
	const { slug } = useTypedParams(Root.Vote)
	const {
		isDataReceived: isUserDataReceived,
		user,
		loading: userLoading,
	} = useAuth()
	const client = useApolloClient()
	const { send } = useAnalytics()

	/**
	 * To re-enable promoted entry view, add entryId from
	 * useTypedSearchParams(Root.Vote) and pass it into a query variable. *
	 */

	const { error, data, previousData, loading } = useQuery(VotesDocument, {
		variables: {
			slug,
			limit: DEFAULT_ENTRIES_LIMIT,
			from: offset,
		},
		errorPolicy: "none",
		ssr: false,
		notifyOnNetworkStatusChange: true,
		context: {
			skipErrors: [
				CustomErrorResponses.IpError,
				CustomErrorResponses.CaptchaError,
			],
		} as ApolloErrorContext,
		onCompleted: data => {
			const anonymousAlreadyVoted =
				!user &&
				isUserDataReceived &&
				data?.challengeEntriesForVoting?.info?.votingStats.totalVoted

			if (data.challenge && !anonymousAlreadyVoted) {
				send(
					prepareAnalytics(
						data.challenge,
						data.challengeEntriesForVoting,
					),
				)
			}
		},
	})

	const actualOffset =
		data?.challengeEntriesForVoting?.info?.pagination.offset

	if (typeof actualOffset === "number" && offset !== actualOffset) {
		client.writeQuery({
			query: VotesDocument,
			variables: {
				slug,
				limit: DEFAULT_ENTRIES_LIMIT,
				from: actualOffset,
			},
			data,
		})
	}

	const currentData = data || previousData

	const results = currentData?.challengeEntriesForVoting.results
	const info = currentData?.challengeEntriesForVoting.info

	return {
		data,
		info,
		loading: loading || userLoading,
		errors: error ? getErrorCodes(error) : [],
		promotion: normalizeEntry(results?.promotedEntry),
		isDataReceived: !!currentData && isUserDataReceived,
		entriesForVoting: results?.entries.map(normalizeEntry).filter(Boolean),
		guestVoting: results?.guestVoting,
	}
}
